/* LLCL Bag V2 / page-scoped progressive enhancement.
 * Shopify owns the cart; native Prestige owns quantity changes and coupon transport.
 * Variant replacement adds first, then removes the old line only after success.
 */
const initialized = new WeakSet();
function mount() {
  const page = document.querySelector('[data-ll-bag-page]');
  if (!page || initialized.has(page)) return;
  initialized.add(page);
  page.querySelectorAll('[data-bag-variant]').forEach(node => { node.hidden = false; });
  const es = page.dataset.language === 'es';
  const root = window.Shopify?.routes?.root || '/';
  const words = es ? {
    pending: 'Actualizando tu selecci\u00f3n\u2026', timeout: 'No pudimos confirmar el cambio. Recarga tu selecci\u00f3n antes de ir al pago.',
    failed: 'No pudimos confirmar el cambio de variante. Revisa la selecci\u00f3n actual.', partial: 'La nueva variante se a\u00f1adi\u00f3, pero no pudimos confirmar la retirada de la anterior. Revisa ambas piezas.', changed: 'Tu selecci\u00f3n ha cambiado. Revisa las cantidades antes de editar.'
  } : { pending: 'Updating your selection\u2026', timeout: 'We could not confirm the change. Reload your selection before checkout.', failed: 'We could not confirm the variant change. Review your current selection.', partial: 'The new variant was added, but removal of the previous one could not be confirmed. Please review both pieces.', changed: 'Your selection has changed. Review the quantities before editing.' };
  let pending = false;
  let timer = 0;
  const status = page.querySelector('[data-bag-status]');
  const error = page.querySelector('[data-bag-error]');
  function showError(message) { error.querySelector('[data-bag-error-copy]').textContent = message; error.hidden = false; }
  try { const message = sessionStorage.getItem('ll-bag-notice'); if (message) { showError(message); sessionStorage.removeItem('ll-bag-notice'); } } catch (_) { /* Storage is optional. */ }
  function begin() {
    pending = true;
    page.setAttribute('aria-busy', 'true');
    page.querySelector('[data-bag-checkout]')?.setAttribute('aria-disabled', 'true');
    error.hidden = true;
    status.textContent = words.pending;
    clearTimeout(timer);
    timer = setTimeout(() => { if (pending) reloadWithNotice(words.timeout); }, 15000);
  }
  function release() {
    pending = false; clearTimeout(timer); page.removeAttribute('aria-busy');
    page.querySelector('[data-bag-checkout]')?.removeAttribute('aria-disabled'); status.textContent = '';
  }
  function block(event) { event.preventDefault(); event.stopImmediatePropagation(); }
  async function jsonRequest(path, body) {
    const response = await fetch(root + path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) } : { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : words.failed);
    return data;
  }
  function reloadWithNotice(message) {
    try { sessionStorage.setItem('ll-bag-notice', message); } catch (_) { showError(message); }
    location.reload();
  }
  async function replaceVariant(button) {
    const row = button.closest('[data-bag-key]');
    const select = row.querySelector('[data-bag-variant-select]');
    if (!select.value || select.value === select.dataset.current || select.selectedOptions[0]?.disabled) return;
    begin();
    let added = false;
    let mutationStarted = false;
    try {
      const cart = await jsonRequest('cart.js');
      const line = cart.items.find(item => item.key === row.dataset.bagKey);
      const displayedQuantity = Number(row.querySelector('input[data-line-key]')?.defaultValue);
      if (!line || line.quantity !== displayedQuantity || String(line.variant_id) !== select.dataset.current) { reloadWithNotice(words.changed); return; }
      const next = { id: select.value, quantity: line.quantity, properties: line.properties || {} };
      const plan = line.selling_plan_allocation?.selling_plan?.id;
      if (plan) next.selling_plan = plan;
      mutationStarted = true;
      await jsonRequest('cart/add.js', { items: [next] });
      added = true;
      await jsonRequest('cart/change.js', { id: line.key, quantity: 0 });
      location.reload();
    } catch (reason) {
      if (mutationStarted) { reloadWithNotice(added ? words.partial : words.failed); return; }
      release(); showError(reason.message || words.failed);
    }
  }
  page.addEventListener('line-item:will-change', begin);
  // Prestige emits cart:change AFTER native quantity changes settle.
  // Without this release, the checkout button can remain gated indefinitely.
  const settlementListeners = new AbortController();
  document.addEventListener('cart:change', event => {
    if (page.isConnected && pending && event.detail?.baseEvent === 'line-item:change') release();
  }, { signal: settlementListeners.signal });
  document.addEventListener('cart:error', event => {
    if (!page.isConnected || !pending) return;
    release();
    const reason = event.detail?.error;
    showError(typeof reason === 'string' ? reason : reason?.description || words.failed);
  }, { signal: settlementListeners.signal });
  const observer = new MutationObserver(records => {
    if (pending && records.some(record => [...record.addedNodes].some(node => node instanceof Element && node.matches('[role="alert"]') && node.previousElementSibling?.matches('line-item-quantity')))) release();
  });
  observer.observe(page, { subtree: true, childList: true });
  page.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (pending && target.closest('line-item-quantity, [data-bag-checkout], [data-bag-variant-save], [data-bag-apply]')) { block(event); return; }
    const save = target.closest('[data-bag-variant-save]');
    if (save) { block(event); replaceVariant(save); return; }
    if (target.closest('[data-bag-apply]')) {
      block(event); const input = page.querySelector('cart-discount-field input:not([type="hidden"])');
      if (input?.value.trim()) input.dispatchEvent(new Event('change', { bubbles: true }));
      else input?.focus();
    }
  }, true);
  page.addEventListener('change', event => {
    if (!event.target.matches('line-item-quantity input, cart-discount-field input:not([type="hidden"])')) return;
    if (pending) { block(event); return; }
    if (event.target.closest('cart-discount-field')) begin();
  }, true);
  page.addEventListener('cart:discount:error', release);
  page.addEventListener('submit', event => {
    if (!event.target.matches('[data-bag-form]')) return;
    if (pending) { block(event); return; }
    const dirty = [...page.querySelectorAll('line-item-quantity input[data-line-key]')].find(input => input.value !== input.defaultValue);
    if (dirty && customElements.get('line-item-quantity')) { block(event); dirty.dispatchEvent(new Event('change', { bubbles: true })); }
  }, true);
  page.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('cart-discount-field input:not([type="hidden"])')) { block(event); if (!pending && event.target.value.trim()) event.target.dispatchEvent(new Event('change', { bubbles: true })); }
  }, true);
  const recommendations = page.querySelector('[data-bag-recommendations][data-url]');
  if (recommendations) {
    fetch(recommendations.dataset.url).then(response => { if (!response.ok) throw new Error('Recommendations unavailable'); return response.text(); }).then(text => {
      const content = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-bag-recommendations-content]');
      if (content && recommendations.isConnected) recommendations.replaceChildren(document.importNode(content, true));
    }).catch(() => { recommendations.hidden = true; });
  }
  page.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => { observer.disconnect(); clearTimeout(timer); settlementListeners.abort(); }, { once: true });
}
mount();
document.addEventListener('shopify:section:load', mount);
